/**
 * Journey Detail Page
 *
 * View journey details and play through quiz sequence.
 * Redesigned with roadmap visualization and exciting progress tracking!
 */

import { useParams, useNavigate } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "@/db/database";
import { AppHeader } from "@/components/layout/AppHeader";
import { Footer } from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Play,
  CheckCircle2,
  ArrowLeft,
  Award,
  Target,
  Zap,
  Trophy,
} from "lucide-react";
import { toast } from "sonner";
import { getJourneyFlag, getQuizFlag } from "@/utils/quizMetadata";

export function JourneyDetailPage() {
  const { journeyId } = useParams<{ journeyId: string }>();
  const navigate = useNavigate();

  // Load journey
  const journey = useLiveQuery(
    () => (journeyId ? db.journeys.get(journeyId) : undefined),
    [journeyId]
  );

  const configs = useLiveQuery(() => db.quiz_configs.toArray());
  const sessions = useLiveQuery(() => db.quiz_sessions.toArray());

  if (!journeyId) {
    return null;
  }

  if (!journey || !configs) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <AppHeader />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-muted-foreground">Loading journey...</p>
        </div>
        <Footer />
      </div>
    );
  }

  // Calculate progress
  const completedCount = journey.quizzes.filter((q) => q.isCompleted).length;
  const totalCount = journey.quizzes.length;
  const progressPercentage =
    totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  const isComplete = completedCount === totalCount;

  // Find next uncompleted quiz
  const nextQuiz = journey.quizzes.find((q) => !q.isCompleted);

  // Start quiz and mark as part of journey
  const handleStartQuiz = async (quizId: string, quizOrder: number) => {
    const config = configs.find((c) => c.signature === quizId);
    if (!config) {
      toast.error("Quiz not found");
      return;
    }

    // Navigate to quiz play page with journey context
    navigate(
      `/quiz/play?signature=${quizId}&journeyId=${journeyId}&journeyOrder=${quizOrder}`
    );
  };

  // Calculate estimated time
  const estimatedMinutes = journey.quizzes.reduce((total, ref) => {
    const quiz = configs.find((c) => c.signature === ref.quizId);
    if (!quiz) return total;
    // Estimate 30 seconds per question
    return total + Math.ceil((quiz.eligibleCount * 30) / 60);
  }, 0);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Global Navigation */}
      <AppHeader />

      {/* Hero Header */}
      <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-background border-b">
        <div className="container mx-auto p-4 md:p-6 max-w-4xl">
          <Button variant="ghost" onClick={() => navigate("/journeys/my-journeys")} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to My Journeys
          </Button>

          <div className="flex items-start justify-between gap-4 mb-6">
            <div className="flex-1">
              <h1 className="text-4xl font-bold mb-2">
                <span className="mr-3">{getJourneyFlag(journey, configs)}</span>
                {journey.title}
              </h1>
              {journey.description && (
                <p className="text-lg text-muted-foreground">{journey.description}</p>
              )}
            </div>
            {isComplete && (
              <div className="bg-green-500 text-white rounded-full p-4 shadow-lg">
                <Trophy className="w-8 h-8" />
              </div>
            )}
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-background/50 backdrop-blur rounded-lg p-4 border">
              <div className="text-3xl font-bold text-primary">{progressPercentage}%</div>
              <div className="text-sm text-muted-foreground">Complete</div>
            </div>
            <div className="bg-background/50 backdrop-blur rounded-lg p-4 border">
              <div className="text-3xl font-bold">{completedCount}/{totalCount}</div>
              <div className="text-sm text-muted-foreground">Quizzes Done</div>
            </div>
            <div className="bg-background/50 backdrop-blur rounded-lg p-4 border">
              <div className="text-3xl font-bold">~{estimatedMinutes}m</div>
              <div className="text-sm text-muted-foreground">Time Left</div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="relative h-3 bg-muted rounded-full overflow-hidden mb-4">
            <div
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary to-primary/60 transition-all duration-500"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>

          {/* Next Quiz CTA */}
          {nextQuiz && !isComplete && (() => {
            const config = configs.find((c) => c.signature === nextQuiz.quizId);
            return (
              <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-primary mb-1">Continue Your Journey</div>
                    <div className="font-semibold">{config?.title || "Next Quiz"}</div>
                  </div>
                  <Button size="lg" onClick={() => handleStartQuiz(nextQuiz.quizId, nextQuiz.order)}>
                    <Play className="w-5 h-5 mr-2" />
                    Start Quiz
                  </Button>
                </div>
              </div>
            );
          })()}

          {/* Completion Celebration */}
          {isComplete && (
            <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-lg p-6 text-center">
              <div className="text-6xl mb-3">🎉</div>
              <h3 className="text-2xl font-bold mb-2">Journey Complete!</h3>
              <p className="text-muted-foreground mb-4">
                Congratulations! You've mastered all quizzes in this journey.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button size="lg" variant="outline" className="w-full sm:w-auto">
                  <Trophy className="w-5 h-5 mr-2" />
                  View Certificate
                </Button>
                <Button size="lg" onClick={() => navigate("/journeys/my-journeys")} className="w-full sm:w-auto">
                  Explore More Journeys
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Journey Roadmap */}
      <div className="container mx-auto p-4 md:p-6 max-w-4xl">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl">Quest Roadmap</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Your learning path - follow the sequence for best results
                </p>
              </div>
              <div className="bg-primary/10 px-4 py-2 rounded-full">
                <span className="text-sm font-medium">
                  {totalCount} {totalCount === 1 ? "Quest" : "Quests"}
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {/* Roadmap Visualization */}
            <div className="relative">
              {journey.quizzes.map((ref, index) => {
                const config = configs.find((c) => c.signature === ref.quizId);
                if (!config) return null;

                const isCompleted = ref.isCompleted;
                const isNext = nextQuiz?.quizId === ref.quizId;
                const isLast = index === journey.quizzes.length - 1;

                // Find session for this quiz
                const quizSessions = sessions?.filter((s) => s.quizSignature === ref.quizId);
                const latestSession = quizSessions?.[quizSessions.length - 1];

                return (
                  <div key={ref.quizId} className="relative">
                    {/* Connector Line */}
                    {!isLast && (
                      <div
                        className={`absolute left-8 top-20 bottom-0 w-1 ${
                          isCompleted ? "bg-green-500" : "bg-muted"
                        }`}
                        style={{ height: "calc(100% - 5rem)" }}
                      />
                    )}

                    {/* Quiz Card */}
                    <div className={`relative flex gap-4 mb-8 ${isNext ? "scale-105" : ""} transition-all`}>
                      {/* Step Number Circle */}
                      <div
                        className={`flex-shrink-0 w-16 h-16 rounded-full flex items-center justify-center text-lg font-bold shadow-lg transition-all ${
                          isCompleted
                            ? "bg-gradient-to-br from-green-500 to-green-600 text-white"
                            : isNext
                            ? "bg-gradient-to-br from-primary to-primary/60 text-primary-foreground animate-pulse"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {isCompleted ? <CheckCircle2 className="w-8 h-8" /> : index + 1}
                      </div>

                      {/* Quiz Info Card */}
                      <div
                        className={`flex-1 rounded-xl border p-5 transition-all cursor-pointer hover:shadow-lg ${
                          isNext
                            ? "border-primary bg-primary/5 shadow-md"
                            : isCompleted
                            ? "border-green-500/30 bg-green-500/5"
                            : "bg-card"
                        }`}
                        onClick={() => handleStartQuiz(ref.quizId, ref.order)}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="text-xl font-semibold">
                                <span className="mr-2">{getQuizFlag(config)}</span>
                                {config.title}
                              </h3>
                              {isNext && (
                                <span className="bg-primary text-primary-foreground px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                                  <Zap className="w-3 h-3" />
                                  Next Up
                                </span>
                              )}
                              {isCompleted && (
                                <span className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                                  <CheckCircle2 className="w-3 h-3" />
                                  Complete
                                </span>
                              )}
                            </div>

                            <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                              <span className="flex items-center gap-1">
                                <Target className="w-4 h-4" />
                                {config.eligibleCount} questions
                              </span>
                              {latestSession && (
                                <span className="flex items-center gap-1">
                                  <Award className="w-4 h-4" />
                                  {latestSession.accuracyRate.toFixed(0)}% accuracy
                                </span>
                              )}
                              <span>~{Math.ceil((config.eligibleCount * 30) / 60)}m</span>
                            </div>

                            {config.description && (
                              <p className="text-sm text-muted-foreground line-clamp-2">
                                {config.description}
                              </p>
                            )}
                          </div>

                          {/* Action Button */}
                          <Button
                            size="lg"
                            variant={isCompleted ? "outline" : isNext ? "default" : "secondary"}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStartQuiz(ref.quizId, ref.order);
                            }}
                          >
                            <Play className="w-5 h-5 mr-2" />
                            {isCompleted ? "Retry" : "Start"}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Motivational Tips */}
        <div className="grid md:grid-cols-2 gap-4 mt-6">
          <Card className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="text-2xl">💡</div>
                <div>
                  <h4 className="font-semibold mb-1">Learning Tip</h4>
                  <p className="text-sm text-muted-foreground">
                    Following the sequence helps build knowledge systematically,
                    but feel free to jump to any quiz you'd like!
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-950">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="text-2xl">🎯</div>
                <div>
                  <h4 className="font-semibold mb-1">Pro Tip</h4>
                  <p className="text-sm text-muted-foreground">
                    Complete quizzes in one sitting when possible. Momentum helps
                    retention and keeps you engaged!
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}
